/* global window */

import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';

import { encodeSize, encodeLocation, getStaticMapUrl } from './utils';

const MAIN_COLOR = '0xd22a15';
const DEFAULT_ZOOM = 12;

const numberOrStringPropType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

export default class StaticMap extends React.Component {
  static propTypes = {
    apiKey: PropTypes.string,
    width: numberOrStringPropType.isRequired,
    height: numberOrStringPropType.isRequired,
    zoom: PropTypes.number,
    center: PropTypes.shape({
      latitude: numberOrStringPropType,
      longitude: numberOrStringPropType,
    }),
    centerOnActiveMarker: PropTypes.bool,
    markers: PropTypes.arrayOf(
      PropTypes.shape({
        latitude: numberOrStringPropType,
        longitude: numberOrStringPropType,
        active: PropTypes.bool,
      })
    ),
  };

  static defaultProps = {
    apiKey: global.CLIENT_ENV.GOOGLE_MAPS_API_KEY,
  };

  state = {
    parentWidth: 400,
    parentHeight: 400,
  };

  // istanbul ignore next
  componentDidMount() {
    window.addEventListener('resize', this.onResize, false);
  }

  // istanbul ignore next
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize, false);
  }

  onResize = throttle(
    // istanbul ignore next
    () => {
      if (!this.imgNode || !this.imgNode.parentNode) {
        return;
      }
      const { width, height } = this.imgNode.parentNode.getBoundingClientRect();
      this.setState({ parentWidth: width, parentHeight: height });
    },
    100
  );

  setImgNode =
    // istanbul ignore next
    (node) => {
      this.imgNode = node;
      this.onResize();
    };

  getSize() {
    let { width, height } = this.props;
    // istanbul ignore next
    if (width === '100%') {
      width = this.state.parentWidth;
    }
    // istanbul ignore next
    if (height === '100%') {
      height = this.state.parentHeight;
    }
    return encodeSize(width, height);
  }

  getCenter() {
    const { center, centerOnActiveMarker, markers } = this.props;
    if (center) {
      return encodeLocation(center.latitude, center.longitude);
    }
    if (!markers || !centerOnActiveMarker) {
      return undefined;
    }
    const activeMarker = markers.find((marker) => marker.active);
    if (!activeMarker) {
      return undefined;
    }
    return encodeLocation(activeMarker.latitude, activeMarker.longitude);
  }

  getZoom() {
    const { zoom, markers } = this.props;
    if (zoom) {
      return zoom;
    }
    if (markers && markers.length > 1) {
      return undefined;
    }
    return DEFAULT_ZOOM;
  }

  getMarkers() {
    const { markers } = this.props;
    if (!markers || markers.length === 0) {
      return undefined;
    }
    const activeMarker = markers.find((marker) => marker.active);
    return markers.map((marker) => ({
      location: encodeLocation(marker.latitude, marker.longitude),
      color: marker.active || !activeMarker ? MAIN_COLOR : 'gray',
      size: marker.active || !activeMarker ? 'mid' : 'small',
    }));
  }

  // istanbul ignore next
  getScale() {
    if (typeof window === 'undefined' || typeof window.devicePixelRatio === 'undefined') {
      return 1;
    }
    return Math.max(window.devicePixelRatio, 2);
  }

  getSrc() {
    return getStaticMapUrl({
      key: this.props.apiKey,
      size: this.getSize(),
      center: this.getCenter(),
      zoom: this.getZoom(),
      markers: this.getMarkers(),
      scale: this.getScale(),
    });
  }

  render() {
    const { apiKey, height, zoom, center, centerOnActiveMarker, markers, ...props } = this.props;
    const style = {};
    // istanbul ignore next
    if (height === '100%') {
      style.height = this.state.parentHeight;
    }
    return <img alt="Map" style={style} {...props} src={this.getSrc()} ref={this.setImgNode} />;
  }
}
